import { Building2, CheckCircle, Clock, BadgePoundSterling, ShieldCheck, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { companyInfo } from '../mockData';

export const Landlords = () => {
  const benefits = [
    {
      icon: Clock,
      title: "Fast 24-48 Hour Turnaround",
      description: "We understand you need certificates quickly. Priority service for letting agents and landlords."
    },
    {
      icon: BadgePoundSterling,
      title: "Bulk Property Discounts",
      description: "Managing multiple properties? We offer competitive rates for bulk bookings and ongoing partnerships."
    },
    {
      icon: ShieldCheck,
      title: "Full Compliance Guaranteed",
      description: "NICEIC approved work ensuring all properties meet current electrical safety regulations."
    },
    {
      icon: Building2,
      title: "HMO Specialists",
      description: "Experienced with HMO requirements including emergency lighting, fire alarm testing, and more."
    }
  ];

  const services = [
    "EICR (Electrical Installation Condition Report)",
    "Emergency Lighting Testing & Certification",
    "Fire Alarm Testing",
    "Consumer Unit Upgrades",
    "Remedial Work Following Inspections",
    "Ongoing Maintenance Contracts",
    "Priority Emergency Callouts"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-700 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Your Trusted Electrical Compliance Partner
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Serving landlords, letting agents, and property managers across Greater London with fast, reliable, and fully certified electrical services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="w-full sm:w-auto gap-2 bg-white text-blue-600 hover:bg-gray-100">
                  <Phone className="h-5 w-5" />
                  Call {companyInfo.phone}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-white text-white hover:bg-blue-700">
                  Get a Free Quote
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Letting Agents Choose Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We understand the unique needs of property professionals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="text-center border-gray-200">
                  <CardHeader>
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Stay Compliant, Stay Protected
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Since April 2021, all landlords in England must have a valid EICR for their rental properties. 
                Non-compliance can result in fines up to £30,000 and serious safety risks.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We make compliance simple with fast turnaround times, competitive pricing, and professional 
                service you can trust. Our NICEIC-approved electricians ensure your properties meet all current 
                safety standards.
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8">
                <h3 className="font-semibold text-gray-900 mb-2">EICR Requirements:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Every 5 years for rental properties</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>After any major electrical work</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1589330694653-ded6df03f754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxjb21wbGlhbmNlJTIwY2VydGlmaWNhdGUlMjBkb2N1bWVudCUyMG9mZmljaWFsJTIwcGFwZXJ3b3JrfGVufDB8fHx8MTc4NjYzNDc0NHww&ixlib=rb-4.1.0&q=85"
                alt="Electrical inspection"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services for Landlords */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Complete Landlord Services
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to keep your properties safe and compliant
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Partner with a Reliable Contractor
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We work with letting agents and property managers across London, providing consistent, 
              high-quality service for single properties or entire portfolios.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-8 mb-12">
              <div>
                <p className="text-4xl font-bold text-blue-600 mb-2">24-48hr</p>
                <p className="text-gray-600">Average turnaround</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-blue-600 mb-2">5.0★</p>
                <p className="text-gray-600">Google rating</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-blue-600 mb-2">100%</p>
                <p className="text-gray-600">NICEIC certified</p>
              </div>
            </div>

            <Link to="/contact">
              <Button size="lg" className="gap-2 bg-blue-600 hover:bg-blue-700">
                Book Your Electrical Compliance Partner Today
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
